/**
 * Month name resolution with partial matching support
 */

import { MONTH_NAMES } from './constants.js';
import { escRx } from './date-utils.js';

// MONTH_NAMES is static, so the resolver tables are identical for every call.
// Compute once on first invocation; reuse forever (saves ~1–3ms per keystroke).
let _resolverCache = null;

/**
 * Build month name lookup tables with partial matching support
 * @returns {Object} { allMonthPats, monthToNum, prefixToMonth, monthRxStr, resolveMonth }
 */
export function buildMonthResolver() {
    if (_resolverCache) return _resolverCache;
    const allMonthPats = [];
    const monthToNum = {};
    const prefixToMonth = new Map(); // Map<prefix, monthNum> for resolving partial matches
    
    // First pass: collect all full month names
    for (const mNum in MONTH_NAMES) {
        const mData = MONTH_NAMES[mNum];
        for (const lang in mData) {
            for (const name of mData[lang]) {
                const lowerName = name.toLowerCase();
                allMonthPats.push(escRx(lowerName));
                monthToNum[lowerName] = parseInt(mNum, 10);
            }
        }
    }
    
    // Second pass: generate partial prefixes (minimum 3 characters)
    // For each month name, generate all prefixes from 3 chars to full length
    // When resolving, use the longest matching prefix
    for (const mNum in MONTH_NAMES) {
        const mData = MONTH_NAMES[mNum];
        const monthNum = parseInt(mNum, 10);
        
        for (const lang in mData) {
            for (const name of mData[lang]) {
                const lowerName = name.toLowerCase();
                // Generate prefixes from 3 characters up to full name length
                for (let len = 3; len <= lowerName.length; len++) {
                    const prefix = lowerName.substring(0, len);
                    // Store prefix -> month mapping (will use longest match later)
                    if (!prefixToMonth.has(prefix) || prefixToMonth.get(prefix) === monthNum) {
                        prefixToMonth.set(prefix, monthNum);
                        // Add to patterns only if it's unique or matches this month
                        if (!monthToNum.hasOwnProperty(prefix)) {
                            allMonthPats.push(escRx(prefix));
                            monthToNum[prefix] = monthNum;
                        }
                    }
                }
            }
        }
    }
    
    // Sort patterns by length (longest first) so regex matches longest prefix first
    const monthRxStr = allMonthPats.sort((a, b) => b.length - a.length).join('|');
    
    /**
     * Resolve month from matched string (handles partial matches)
     */
    function resolveMonth(matchedMonthStr) {
        const lower = matchedMonthStr.toLowerCase();
        // Try exact match first
        if (monthToNum.hasOwnProperty(lower)) {
            return monthToNum[lower];
        }
        // Try to find longest matching prefix (regex already matched longest, but check for safety)
        // Since patterns are sorted by length (longest first), regex should match longest prefix
        // But we also check here to be safe
        let longestMatch = '';
        let longestMatchMonth = null;
        for (const [prefix, monthNum] of prefixToMonth.entries()) {
            if (lower.startsWith(prefix) && prefix.length > longestMatch.length) {
                longestMatch = prefix;
                longestMatchMonth = monthNum;
            }
        }
        return longestMatchMonth;
    }
    
    _resolverCache = {
        allMonthPats,
        monthToNum,
        prefixToMonth,
        monthRxStr,
        resolveMonth
    };
    return _resolverCache;
}

